import { z } from "zod";

// Redirect status code enum
export const RedirectStatusCodeSchema = z.enum(["301", "302", "307", "308"]);

// Redirect lookup response (public API)
export const RedirectLookupResponseSchema = z.preprocess(
    (val) => {
        // If val is null, undefined, or doesn't have redirect fields, return null
        if (val === null || val === undefined) {
            return null;
        }
        if (typeof val === 'object' && val !== null && 'target_path' in val && 'status_code' in val) {
            return val;
        }
        // For fallback objects or other structures, return null
        return null;
    },
    z.union([
        z.object({
            target_path: z.string(),
            status_code: z.number().int().min(301).max(308),
        }),
        z.null(),
    ])
);

// Redirect response (admin API)
export const RedirectSchema = z.object({
    id: z.number(),
    source_path: z.string(),
    target_path: z.string(),
    status_code: z.number().int().min(301).max(308),
    is_active: z.boolean(),
    is_effective: z.boolean(),
    is_expired: z.boolean(),
    hits_count: z.number().int().min(0),
    expires_at: z.string().nullable(),
    created_at: z.string(),
    updated_at: z.string(),
});

// Create redirect request body
export const CreateRedirectRequestSchema = z.object({
    source_path: z.string().min(1).refine((val) => val.startsWith("/"), {
        message: "The source path must start with a forward slash (/).",
    }),
    target_path: z.string().min(1).max(2048),
    status_code: z.number().int().min(301).max(308).optional().default(301),
    is_active: z.boolean().optional().default(true),
    expires_at: z.string().nullable().optional(),
});

// Update redirect request body
export const UpdateRedirectRequestSchema = z.object({
    source_path: z.string().min(1).refine((val) => val.startsWith("/"), {
        message: "The source path must start with a forward slash (/).",
    }).optional(),
    target_path: z.string().min(1).max(2048).optional(),
    status_code: z.number().int().min(301).max(308).optional(),
    is_active: z.boolean().optional(),
    expires_at: z.string().nullable().optional(),
});

// TypeScript type exports
export type RedirectStatusCode = z.infer<typeof RedirectStatusCodeSchema>;
export type RedirectLookupResponse = z.infer<typeof RedirectLookupResponseSchema>;
export type Redirect = z.infer<typeof RedirectSchema>;
export type CreateRedirectRequest = z.infer<typeof CreateRedirectRequestSchema>;
export type UpdateRedirectRequest = z.infer<typeof UpdateRedirectRequestSchema>;

